/**
 * The six reads behind the triage view, each landing independently.
 *
 * NOT `await Promise.allSettled(...)` then one `setState`: that paces the whole page to the
 * slowest read, and `/liability` (a scan of every balance document) will be the slowest.
 * Each thunk writes its own slot, so six requests fly at once and each line paints the
 * instant ITS answer arrives.
 *
 * `Promise.all` is banned here and this comment is the reason: one refusal must never blank
 * the other five. An operator whose dispute-rate read failed still needs to be told the books
 * are out of balance.
 *
 * Every signal keeps its own `{data, error, code, fetchedAt}` so the view can render the third
 * state — not-known — per line rather than for the page.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { extractGatewayErrorCode } from "../../gateway/errors";

/** Yesterday, UTC. `daily_reconcile` defaults to yesterday, so today's report does not exist
 *  yet — and the date is rendered beside the line, because an operator must never have to
 *  wonder which day was checked. */
export function reconciliationDate(now = new Date()) {
  const d = new Date(now.getTime() - 24 * 3600 * 1000);
  return d.toISOString().slice(0, 10);
}

const SIGNALS = ["liability", "books", "reconciliation", "payouts", "disputeRates", "events"];

const blank = () => SIGNALS.reduce((acc, k) => {
  acc[k] = { data: null, error: null, code: null, fetchedAt: null, loading: true };
  return acc;
}, {});

export function useOverviewTriage(gatewayApi) {
  const [signals, setSignals] = useState(blank);
  const seq = useRef(0);
  const alive = useRef(true);
  // RE-ARMED ON EVERY MOUNT, and that is the whole point. Written as
  // `useEffect(() => () => { alive.current = false; }, [])` this was a LATCH, not a
  // liveness check: `StrictMode` (src/index.js:50) mounts, runs the cleanup, and re-mounts,
  // so `alive` went false on the first cleanup and nothing ever set it back. Every
  // `.then(...)` below was then discarded, `loading` stayed true for ever, and the whole
  // redesigned overview sat on "reading…" over a ledger that had answered in 3 ms. Jest
  // does not wrap in StrictMode, so the unit tests were green throughout — which is why
  // `GatewayConsole.strictMode.test.js` now mounts inside one.
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const date = useMemo(() => reconciliationDate(), []);

  const reload = useCallback(() => {
    const mine = ++seq.current;
    setSignals(blank());
    const put = (key) => (patch) => {
      if (!alive.current || mine !== seq.current) return;
      setSignals((s) => ({ ...s, [key]: { ...s[key], ...patch, loading: false } }));
    };
    const run = (key, thunk) => Promise.resolve().then(thunk).then(
      (data) => put(key)({ data, error: null, code: null, fetchedAt: new Date() }),
      (error) => put(key)({ error, code: extractGatewayErrorCode(error) }),
    );

    return Promise.allSettled([
      run("liability", () => gatewayApi.adminLiability()),
      run("books", () => gatewayApi.adminTrialBalance()),
      // NO `run=true`. The overview must never trigger a Stripe read as a side effect of
      // someone opening a page.
      run("reconciliation", () => gatewayApi.adminReconciliation(date)),
      run("payouts", () => gatewayApi.adminPendingPayouts()),
      run("disputeRates", () => gatewayApi.adminDisputeRates(90)),
      run("events", () => gatewayApi.adminDeadLetterWebhooks()),
    ]);
  }, [gatewayApi, date]);

  useEffect(() => { reload(); }, [reload]);

  /** The OLDEST successful read, not the newest: the page must not claim to be fresher than
   *  its stalest number. */
  const oldestFetchedAt = useMemo(() => {
    const stamps = SIGNALS.map((k) => signals[k].fetchedAt).filter(Boolean);
    return stamps.length ? new Date(Math.min(...stamps.map((d) => d.getTime()))) : null;
  }, [signals]);

  const anyLoading = SIGNALS.some((k) => signals[k].loading);

  return { signals, reload, date, oldestFetchedAt, anyLoading };
}

export { SIGNALS };
